import { appState, deviceAliases, commentCache } from '../core/state';
import { can } from '../core/access';
import { esc, openModal, closeModal, onModalClose, showModalErr } from '../ui/modal';
import { t } from '../ui/lang';
import { inlineStreams } from '../stream/inline-stream';
import { renderFarmDevices } from '../farms/farms-view';
export function showRenameDeviceModal(udid, current) {
    if (!can('device.rename'))
        return;
    const modal = openModal(`
        <h2 class="modal__title">${t('devices.rename_device')}</h2>
        <form id="rename-form" class="modal__form" novalidate>
            <div class="modal__field">
                <label>${t('devices.device_name')} <span class="modal__hint">${t('devices.rename_hint')}</span></label>
                <input id="rename-input" type="text" autocomplete="off" value="${esc(current)}" maxlength="20" placeholder="${t('devices.custom_name')}" />
            </div>
            <div id="rename-error" class="modal__error" style="display:none"></div>
            <div class="modal__actions">
                <button type="button" class="modal__cancel" id="rename-cancel">${t('common.cancel')}</button>
                <button type="submit" class="modal__submit">${t('common.save')}</button>
            </div>
        </form>
    `);
    const input = modal.querySelector('#rename-input');
    input.focus();
    input.select();
    modal.querySelector('#rename-cancel').addEventListener('click', closeModal);
    modal.querySelector('#rename-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const name = input.value.trim();
        const errorEl = modal.querySelector('#rename-error');
        const btn = modal.querySelector('.modal__submit');
        errorEl.style.display = 'none';
        if (name.length > 20) {
            showModalErr(errorEl, t('devices.name_too_long'));
            return;
        }
        btn.disabled = true;
        try {
            const res = await fetch(`/api/devices/${encodeURIComponent(udid)}/name`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name }),
            });
            const data = await res.json();
            if (!res.ok) {
                showModalErr(errorEl, data.error ?? t('devices.failed_to_rename'));
                return;
            }
            if (name)
                deviceAliases.set(udid, name);
            else
                deviceAliases.delete(udid);
            closeModal();
            if (appState.selectedFarm !== null)
                renderFarmDevices();
        }
        catch {
            showModalErr(errorEl, t('devices.network_error'));
        }
        finally {
            btn.disabled = false;
        }
    });
}
export async function showAppsModal(udid, displayName) {
    const modal = openModal(`
        <h2 class="modal__title">${t('devices.installed_apps_title', { name: esc(displayName) })}</h2>
        <div id="apps-loading" class="modal__text" style="text-align:center;padding:24px 0">${t('devices.loading_packages')}</div>
        <div id="apps-list" class="apps-list" style="display:none"></div>
        <div id="apps-error" class="modal__error" style="display:none"></div>
    `);
    modal.classList.add('modal--wide');
    const loadingEl = modal.querySelector('#apps-loading');
    const listEl = modal.querySelector('#apps-list');
    const errorEl = modal.querySelector('#apps-error');
    try {
        const res = await fetch(`/api/devices/${encodeURIComponent(udid)}/apps`);
        const data = await res.json();
        if (!res.ok)
            throw new Error(data.error ?? t('devices.failed_to_load_apps'));
        const packages = data.packages ?? [];
        loadingEl.style.display = 'none';
        listEl.style.display = '';
        if (packages.length === 0) {
            listEl.innerHTML = `<p class="modal__text" style="text-align:center;padding:16px 0">${t('devices.no_user_apps')}</p>`;
        }
        else {
            listEl.innerHTML = packages.map((pkg) => `<div class="app-list-item">
                    <span class="app-pkg-name">${esc(pkg)}</span>
                    <a class="tbl-btn app-apk-btn"
                       href="/api/devices/${encodeURIComponent(udid)}/apps/${encodeURIComponent(pkg)}/apk"
                       title="${t('devices.download_apk')}">↓ APK</a>
                </div>`).join('');
        }
    }
    catch (err) {
        loadingEl.style.display = 'none';
        showModalErr(errorEl, err.message);
    }
}
export function showShellModal(udid, displayName) {
    const modal = openModal(`
        <h2 class="modal__title">${t('devices.adb_shell_title', { name: esc(displayName) })}</h2>
        <pre id="shell-output" class="shell-output"></pre>
        <div class="shell-input-row">
            <span class="shell-prompt">$</span>
            <input id="shell-input" class="shell-input" type="text"
                autocomplete="off" spellcheck="false" placeholder="${t('devices.shell_command_placeholder')}" />
            <button type="button" id="shell-send" class="modal__submit">${t('devices.send')}</button>
        </div>
        <div class="modal__actions">
            <button type="button" class="tbl-btn" id="shell-ctrl-c">Ctrl+C</button>
        </div>
    `);
    modal.classList.add('modal--wide');
    const outputEl = modal.querySelector('#shell-output');
    const inputEl = modal.querySelector('#shell-input');
    const stripAnsi = (s) => s.replace(/\x1b\[[0-9;?]*[a-zA-Z]/g, '')
        .replace(/\x1b\][^\x07\x1b]*(?:\x07|\x1b\\)/g, '')
        .replace(/\x1b[()][A-Z0-9]/g, '')
        .replace(/\x1b./g, '');
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/?action=shell&udid=${encodeURIComponent(udid)}`);
    ws.binaryType = 'arraybuffer';
    ws.onmessage = (ev) => {
        if (!(ev.data instanceof ArrayBuffer))
            return;
        const text = new TextDecoder().decode(ev.data);
        const clean = stripAnsi(text).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        outputEl.textContent += clean;
        outputEl.scrollTop = outputEl.scrollHeight;
    };
    ws.onclose = () => {
        outputEl.textContent += `\n${t('devices.shell_disconnected')}\n`;
        inputEl.disabled = true;
    };
    ws.onerror = () => {
        outputEl.textContent += `\n${t('devices.shell_connection_error')}\n`;
        inputEl.disabled = true;
    };
    const send = () => {
        if (ws.readyState !== WebSocket.OPEN)
            return;
        const text = inputEl.value + '\n';
        ws.send(new TextEncoder().encode(text));
        inputEl.value = '';
    };
    inputEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            send();
        }
        if (e.ctrlKey && e.key === 'c') {
            e.preventDefault();
            if (ws.readyState === WebSocket.OPEN)
                ws.send(new Uint8Array([0x03]));
        }
        if (e.ctrlKey && e.key === 'd') {
            e.preventDefault();
            if (ws.readyState === WebSocket.OPEN)
                ws.send(new Uint8Array([0x04]));
        }
    });
    modal.querySelector('#shell-send').addEventListener('click', send);
    modal.querySelector('#shell-ctrl-c').addEventListener('click', () => {
        if (ws.readyState === WebSocket.OPEN)
            ws.send(new Uint8Array([0x03]));
    });
    // The corner ✕ is the only dismissal here, so it carries the socket teardown.
    onModalClose(modal, () => {
        ws.close();
        closeModal();
    });
    inputEl.focus();
}
/**
 * Shared read-only info modal: a title, label/value rows (device-info-list styling —
 * label left, value right, dividers) and a Close button, plus an optional extra action
 * (e.g. Rename). Used by the normal device card and the duo card so both look the same.
 */
export function showInfoModal(title, rows, extra) {
    const extraBtn = extra ? `<button type="button" class="modal__cancel" id="info-extra">${esc(extra.label)}</button>` : '';
    const modal = openModal(`
        <h2 class="modal__title">${esc(title)}</h2>
        <div class="device-info-list">
            ${rows.map((r) => `<div><span>${esc(r.label)}</span><strong>${esc(r.value)}</strong></div>`).join('')}
        </div>
        ${extraBtn ? `<div class="modal__actions">${extraBtn}</div>` : ''}
    `);
    // Explicit: the extra action is a .modal__cancel, which the ✕ would otherwise take
    // for this card's own dismissal and run instead of closing.
    onModalClose(modal, closeModal);
    if (extra)
        modal.querySelector('#info-extra')?.addEventListener('click', () => { closeModal(); extra.onClick(); });
}
// `noVideo` — a video-less device (aggregator / mobile-farm-client): it never opens
// a scrcpy stream, so Resolution and Video codec are always empty. Drop those two
// rows for it (signals/battery still apply and stay).
export function showDeviceInfoModal(d, displayName, noVideo = false) {
    const battery = d['battery.level'];
    const wifi = d['wifi.signal.level'];
    const cellular = d['cellular.signal.level'];
    const metadata = inlineStreams.get(d.udid)?.metadata;
    const rows = [
        { label: t('devices.device_id'), value: d.udid },
        { label: t('devices.manufacturer'), value: d['ro.product.manufacturer'] || '—' },
        { label: t('devices.model'), value: d['ro.product.model'] || '—' },
    ];
    if (!noVideo) {
        rows.push({ label: t('devices.resolution'), value: metadata ? `${metadata.screenWidth}×${metadata.screenHeight}` : '—' });
        rows.push({ label: t('devices.video_codec'), value: metadata ? metadata.videoCodec : '—' });
    }
    rows.push({ label: t('devices.battery'), value: battery !== undefined ? `${battery}%` : '—' });
    rows.push({ label: t('devices.wifi_signal'), value: wifi !== undefined ? `${wifi}/4` : '—' });
    rows.push({ label: t('devices.cellular_signal'), value: cellular !== undefined ? `${cellular}/4` : '—' });
    const extra = can('device.rename')
        ? { label: t('common.rename'), onClick: () => showRenameDeviceModal(d.udid, displayName) }
        : undefined;
    showInfoModal(displayName, rows, extra);
}
export function showDeviceCommentModal(udid, displayName, current) {
    const modal = openModal(`
        <h2 class="modal__title">${t('devices.comment_title', { name: esc(displayName) })}</h2>
        <form id="comment-form" class="modal__form" novalidate>
            <div class="modal__field">
                <label>${t('devices.comment')} <span class="modal__hint">${t('devices.comment_hint')}</span></label>
                <textarea id="comment-input" class="modal__textarea" rows="12" maxlength="500" placeholder="${t('devices.comment_placeholder')}">${esc(current)}</textarea>
            </div>
            <div id="comment-error" class="modal__error" style="display:none"></div>
            <div class="modal__actions">
                <button type="button" class="modal__cancel" id="comment-cancel">${t('common.cancel')}</button>
                <button type="submit" class="modal__submit">${t('common.save')}</button>
            </div>
        </form>
    `);
    const input = modal.querySelector('#comment-input');
    input.focus();
    modal.querySelector('#comment-cancel').addEventListener('click', closeModal);
    modal.querySelector('#comment-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const comment = input.value.trim();
        const errorEl = modal.querySelector('#comment-error');
        const btn = modal.querySelector('.modal__submit');
        errorEl.style.display = 'none';
        if (comment.length > 500) {
            showModalErr(errorEl, t('devices.comment_too_long'));
            return;
        }
        btn.disabled = true;
        try {
            const res = await fetch(`/api/devices/${encodeURIComponent(udid)}/comment`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ comment }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                showModalErr(errorEl, data.error ?? t('devices.failed_to_save_comment'));
                btn.disabled = false;
                return;
            }
            commentCache.set(udid, comment);
            closeModal();
        }
        catch {
            showModalErr(errorEl, t('devices.network_error'));
            btn.disabled = false;
        }
    });
}
